import { type MouseEvent, type ReactNode, type RefObject } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import ImageIcon from "@mui/icons-material/Image";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ListAltOutlined from "@mui/icons-material/ListAltOutlined";
import PdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  Box,
  Button,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import type { Promisable } from "type-fest";

export interface SupersetDashboardPageInnerProps {
  loading: boolean;
  errorMessage: string | null;
  hasValidDashboardData: boolean;
  title?: string;
  isExporting: boolean;
  exportError: string | null;
  downloadMenuOpen: boolean;
  downloadAnchorEl: HTMLElement | null;
  onDownloadClick: (event: MouseEvent<HTMLButtonElement>) => void;
  onDownloadClose: () => void;
  onExport: (format: "pdf" | "png") => Promisable<void>;
  onViewGlossary?: (() => void) | undefined;
  containerRef: RefObject<HTMLDivElement | null>;
  keepaliveDialog?: ReactNode;
}

export function SupersetDashboardPageInner({
  loading,
  errorMessage,
  hasValidDashboardData,
  title,
  isExporting,
  exportError,
  downloadMenuOpen,
  downloadAnchorEl,
  onDownloadClick,
  onDownloadClose,
  onExport,
  onViewGlossary,
  containerRef,
  keepaliveDialog,
}: SupersetDashboardPageInnerProps) {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (errorMessage || !hasValidDashboardData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          p: 4,
        }}
      >
        {errorMessage ?? "Dashboard is not available"}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {keepaliveDialog}
      {/* Header with dashboard title and download button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 4,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" noWrap sx={{ mr: 2 }}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {exportError && (
            <Box sx={{ color: "error.main", mr: 2, fontSize: "0.875rem" }}>
              {exportError}
            </Box>
          )}
          {onViewGlossary && (
            <Button
              variant="outlined"
              startIcon={<ListAltOutlined />}
              onClick={onViewGlossary}
              sx={{ mr: 1 }}
            >
              View Data Glossary
            </Button>
          )}
          <Button
            id="download-button"
            variant="outlined"
            startIcon={
              isExporting ? <CircularProgress size={16} /> : <DownloadIcon />
            }
            endIcon={<ArrowDownIcon />}
            onClick={onDownloadClick}
            disabled={isExporting}
            aria-controls={downloadMenuOpen ? "download-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={downloadMenuOpen ? "true" : undefined}
          >
            {isExporting ? "Exporting..." : "Download"}
          </Button>
          <Menu
            id="download-menu"
            anchorEl={downloadAnchorEl}
            open={downloadMenuOpen}
            onClose={onDownloadClose}
            slotProps={{
              list: {
                "aria-labelledby": "download-button",
              },
            }}
          >
            <MenuItem
              onClick={() => {
                void onExport("pdf");
              }}
            >
              <ListItemIcon>
                <PdfIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download as PDF</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                void onExport("png");
              }}
            >
              <ListItemIcon>
                <ImageIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download as Image</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      {/* Dashboard container */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          ref={containerRef}
          sx={{
            height: "95%",
            width: "90%",
            "& iframe": {
              width: "100%",
              height: "100%",
              border: "none",
            },
          }}
        />
      </Box>
    </Box>
  );
}
